import React from "react";

import { Radar, TrendingUp } from "lucide-react";

export type BirdsEyeMapEmptyReason =
  | "no-accounts"
  | "no-digest"
  | "cluster-filter";

type BirdsEyeMapEmptyStateProps = {
  variant: "reach" | "revenue";
  reason: BirdsEyeMapEmptyReason;
  clusterLabel?: string;
  accountsWithoutDigestCount?: number;
  totalAccountCount?: number;
  onClearClusterFilter?: () => void;
  /** When true, render only the message card (no plot overlay). */
  standalone?: boolean;
};

const COPY: Record<
  BirdsEyeMapEmptyReason,
  Record<"reach" | "revenue", { title: string; body: string }>
> = {
  "no-accounts": {
    reach: {
      title: "No accounts to plot",
      body: "Choose a portfolio, watchlist, or executive sponsor with accounts to see the Risk–Opportunity map.",
    },
    revenue: {
      title: "No accounts to plot",
      body: "Choose a portfolio, watchlist, or executive sponsor with accounts to see the Revenue map.",
    },
  },
  "no-digest": {
    reach: {
      title: "No map data yet",
      body: "Accounts need intelligence briefing digests before they appear here. Open Attention → Business Health to generate briefings, then return to Bird's Eye.",
    },
    revenue: {
      title: "No revenue metrics to plot",
      body: "Accounts need briefing digests with ARR and NRR metrics. Generate Business Health briefings under Attention, then reopen this map.",
    },
  },
  "cluster-filter": {
    reach: {
      title: "No accounts in this cluster",
      body: "Clear the cluster highlight to show every account on the Risk–Opportunity map again.",
    },
    revenue: {
      title: "No accounts in this cluster",
      body: "Clear the cluster highlight to show every account on the Revenue map again.",
    },
  },
};

const BirdsEyeMapEmptyState: React.FC<BirdsEyeMapEmptyStateProps> = ({
  variant,
  reason,
  clusterLabel,
  accountsWithoutDigestCount = 0,
  totalAccountCount = 0,
  onClearClusterFilter,
  standalone = false,
}) => {
  const Icon = variant === "reach" ? Radar : TrendingUp;
  const copy = COPY[reason][variant];
  const title =
    reason === "cluster-filter" && clusterLabel
      ? `No accounts in ${clusterLabel}`
      : copy.title;

  const body =
    reason === "no-digest" &&
    totalAccountCount > 0 &&
    accountsWithoutDigestCount > 0 ? (
      <>
        {totalAccountCount} account{totalAccountCount !== 1 ? "s" : ""} in
        scope, but none have briefing digests with the metrics needed for this
        map.
      </>
    ) : (
      copy.body
    );

  const card = (
    <div className="w-full max-w-[400px] rounded-xl border border-slate-200/90 bg-white px-6 py-7 text-center shadow-[0_8px_32px_-8px_rgba(15,23,42,0.12)]">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-violet-50 ring-1 ring-violet-100">
        <Icon
          className="h-6 w-6 text-violet-600"
          strokeWidth={1.75}
          aria-hidden
        />
      </div>
      <h3 className="mb-2 text-[15px] font-semibold leading-snug text-slate-900">
        {title}
      </h3>
      <p className="mb-0 text-[13px] leading-relaxed text-slate-500">{body}</p>
      {reason === "cluster-filter" && onClearClusterFilter ? (
        <button
          type="button"
          onClick={onClearClusterFilter}
          className="mt-4 inline-flex items-center rounded-md border border-violet-200 bg-violet-50 px-3 py-1.5 text-[12px] font-semibold text-violet-700 transition-colors hover:bg-violet-100"
        >
          Show all accounts
        </button>
      ) : null}
    </div>
  );

  if (standalone) {
    return (
      <div
        className="flex flex-1 flex-col items-center justify-center px-4 py-16"
        role="status"
        aria-live="polite"
      >
        {card}
      </div>
    );
  }

  return (
    <div
      className="absolute inset-0 z-[6] flex items-center justify-center bg-slate-50/50 p-4 sm:p-6"
      role="status"
      aria-live="polite"
    >
      {card}
    </div>
  );
};

export default BirdsEyeMapEmptyState;
